import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import type { SavePipelineInput } from '@protocols/use-cases';

import { PipelineData } from './pipeline.state';
import { PipelineStepCategory } from '@protocols/domain';

/**
 * A staged draft always carries its id: the client temp id for pipelines
 * created in the UI, the real DB id for edits to persisted pipelines.
 */
export type StagedPipelineDraft = SavePipelineInput & { id: string };

/*
 * Local edit applied to a pipeline that DOES exist in the database. `original`
 * is the pre-edit DB summary; the listing flow needs it to hide the stale DB
 * row, place the edited version at its (re-sorted) position, and keep the
 * total count exact even when the edit moves the pipeline in/out of the active
 * search match. `draft` is the full edit payload, keyed onto `original.id`.
 */
export interface StagedPipelineOverride {
  original: PipelineData;
  draft: StagedPipelineDraft;
}

interface StagedPipelinesStateModel {
  /*
   * Full drafts of pipelines created in the UI (via the editor dialog) but not
   * yet persisted, each carrying its client temp id. Source of truth — the
   * catalog's items adapter is just a per-page snapshot that may surface some
   * of these. They stay listed in the catalog for the rest of the session even
   * when not assigned to a method type; the save cascade will only persist the
   * ones that are still an active part of the protocol.
   */
  pendingPipelines: StagedPipelineDraft[];

  /*
   * Local edits to persisted pipelines, keyed by id. Until persisted, the
   * listing flow shows the (projected) override in place of the DB row sharing
   * this id.
   */
  pipelineOverrides: Record<string, StagedPipelineOverride>;

  /**
   * True while the save cascade persists the staged drafts.
   */
  saving: boolean;

  /*
   * Per-row failures of the last consolidation pass, keyed by the staged id
   * (client temp id for pending drafts, DB id for overrides) — set by the
   * cascade, `StagedResourcesState.saveExceptions` parity.
   */
  saveExceptions: Record<string, Error>;
}

const INITIAL_STATE: StagedPipelinesStateModel = {
  pendingPipelines: [],
  pipelineOverrides: {},
  saving: false,
  saveExceptions: {},
};

/**
 * Shared pipeline staging ledger. One instance is provided by the outermost
 * consumer (the host above `methodPipelineManagerProviders`, alongside
 * `AssignedPipelinesState`) and shared down the dialog stack: drafts survive a
 * manager unmount, and the method-type editor page provides the single shared
 * instance the save cascade reads (mirroring `StagedResourcesState`).
 *
 * Deliberately NOT part of `pipelineStoreProviders` /
 * `methodPipelineManagerProviders`: a per-manager copy would be torn down with
 * each manager and could not survive the dialog stack.
 */
@Injectable()
export class StagedPipelinesState extends ComponentStore<StagedPipelinesStateModel> {
  constructor() {
    super({ ...INITIAL_STATE });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly pendingPipelines$ = this.select((state) => state.pendingPipelines);
  public readonly pipelineOverrides$ = this.select((state) => state.pipelineOverrides);
  public readonly saving$ = this.select((state) => state.saving);
  public readonly saveExceptions$ = this.select((state) => state.saveExceptions);

  // ============================================================================
  // Updaters
  // ============================================================================

  /**
   * Upsert the full draft of a not-yet-persisted pipeline (keyed by its client
   * temp id). Drafts without an id are ignored.
   */
  public readonly stagePending = this.updater((state, draft: SavePipelineInput) => {
    const id = draft.id;
    if (!id) return state;
    const without = state.pendingPipelines.filter((p) => p.id !== id);
    return { ...state, pendingPipelines: [...without, { ...draft, id }] };
  });

  /**
   * Record the full draft of a local edit to a persisted pipeline (keyed by
   * `original.id`; the draft id is normalized onto it).
   */
  public readonly stageOverride = this.updater((state, entry: { original: PipelineData; draft: SavePipelineInput }) => {
    const id = entry.original.id;
    const draft: StagedPipelineDraft = { ...entry.draft, id };
    return {
      ...state,
      pipelineOverrides: { ...state.pipelineOverrides, [id]: { original: entry.original, draft } },
    };
  });

  /**
   * Remove a pipeline's staged entry, whichever slot it occupies
   */
  public readonly unstage = this.updater((state, id: string) => {
    const hasPending = state.pendingPipelines.some((p) => p.id === id);
    const hasOverride = id in state.pipelineOverrides;
    if (!hasPending && !hasOverride) return state;
    const pipelineOverrides = { ...state.pipelineOverrides };
    delete pipelineOverrides[id];
    return {
      ...state,
      pendingPipelines: state.pendingPipelines.filter((p) => p.id !== id),
      pipelineOverrides,
    };
  });

  /**
   * Drop everything staged this session
   */
  public readonly clear = this.updater(() => ({ ...INITIAL_STATE }));

  /**
   * Rewrite label uuids embedded in the staged drafts' structured-mode step
   * categories using the `oldUuid → newUuid` mapping produced by label
   * consolidation (same transform `PipelineEditorState.remapLabelUuids`
   * applies to the live editing session; manual steps carry an opaque `params`
   * blob and are left untouched). Eager — the drafts stay valid even when a
   * later cascade phase fails and the retry runs with an empty map.
   */
  public readonly remapLabelUuids = this.updater((state, mapping: Map<string, string>) => {
    if (mapping.size === 0) return state;

    const remapDraft = (draft: StagedPipelineDraft): StagedPipelineDraft => ({
      ...draft,
      steps: draft.steps.map((step) => {
        if (step.mode !== 'structured') return step;
        return {
          ...step,
          categories: step.categories.map(
            (category) =>
              new PipelineStepCategory({
                uuid: mapping.get(category.uuid) ?? category.uuid,
                name: category.name,
                options: category.options.map((option) => ({
                  uuid: mapping.get(option.uuid) ?? option.uuid,
                  name: option.name,
                  thrdis: option.thrdis,
                })),
              })
          ),
        };
      }),
    });

    const pipelineOverrides: Record<string, StagedPipelineOverride> = {};
    for (const id of Object.keys(state.pipelineOverrides)) {
      const entry = state.pipelineOverrides[id];
      pipelineOverrides[id] = { ...entry, draft: remapDraft(entry.draft) };
    }

    return {
      ...state,
      pendingPipelines: state.pendingPipelines.map(remapDraft),
      pipelineOverrides,
    };
  });

  // ============================================================================
  // Updaters — cascade bookkeeping (StagedResourcesState parity)
  // ============================================================================

  public readonly setSaving = this.updater((state, saving: boolean) => ({ ...state, saving }));

  // Record one row's save failure keyed by its staged id. Does NOT touch
  // `saving` — the cascade flips it once after the batch settles.
  public readonly recordSaveException = this.updater((state, entry: { id: string; error: Error }) => ({
    ...state,
    saveExceptions: { ...state.saveExceptions, [entry.id]: entry.error },
  }));

  // Clear all recorded save failures. No-op when already empty so a fresh `{}`
  // reference can't fire a spurious selector emit.
  public readonly clearSaveExceptions = this.updater((state) =>
    Object.keys(state.saveExceptions).length === 0 ? state : { ...state, saveExceptions: {} }
  );
}
